import { useState } from "react";
import * as XLSX from "xlsx";
import {
  getTransactionByUtr,
  isChargebackExists,
} from "../../../../config/LiveApi";
import { toast } from "react-toastify";

const TransactionCheck = ({ token }) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const styles = {
    container: {
      padding: "24px",
      fontFamily: "Arial",
      background: "#f8fafc",
    },
    heading: {
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "16px",
    },
    button: {
      padding: "8px 14px",
      marginRight: "10px",
      marginTop: "10px",
      border: "none",
      borderRadius: "6px",
      background: "#2563eb",
      color: "#fff",
      cursor: "pointer",
    },
    buttonGreen: {
      padding: "8px 14px",
      marginTop: "10px",
      border: "none",
      borderRadius: "6px",
      background: "#16a34a",
      color: "#fff",
      cursor: "pointer",
    },
    tableWrap: {
      marginTop: "20px",
      maxHeight: "500px",
      overflow: "auto",
      border: "1px solid #ddd",
      borderRadius: "8px",
      background: "#fff",
    },
    th: {
      position: "sticky",
      top: 0,
      background: "#f1f5f9",
      padding: "8px",
      fontSize: "12px",
    },
    td: {
      padding: "8px",
      fontSize: "12px",
      borderBottom: "1px solid #eee",
    },
    notFound: {
      background: "#fee2e2",
    },
    chargeback: {
      background: "#fef3c7",
    },
    amountMismatch: {
      background: "#e0f2fe",
    },
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      if (!json.length) {
        toast.error("Uploaded file is empty");
        return;
      }
      const cols = Object.keys(json[0]);
      const utrKey = cols.find((c) => c.toLowerCase() === "utr");

      if (!utrKey) {
        toast.error("UTR column not found");
        return;
      }

      const newCols = [];
      cols.forEach((c) => {
        newCols.push(c);
        if (c === utrKey) newCols.push("Valid UTR");
      });

      const formatted = json.map((row) => ({
        ...row,
        "Valid UTR": row[utrKey] ? String(row[utrKey]).padStart(12, "0") : "",
      }));

      setData(formatted);
      setColumns(newCols);
    };

    reader.readAsBinaryString(file);
  };

  const markDuplicates = () => {
    if (!data.length) return;

    const countMap = {};

    // count occurrences
    data.forEach((row) => {
      const utr = row["Valid UTR"];
      if (utr) {
        countMap[utr] = (countMap[utr] || 0) + 1;
      }
    });

    // mark duplicates
    const updated = data.map((row) => {
      const utr = row["Valid UTR"];
      return {
        ...row,
        Duplicate: utr && countMap[utr] > 1 ? "Yes" : "No",
      };
    });

    // add column safely
    const newCols = [...columns];
    if (!newCols.includes("Duplicate")) {
      newCols.push("Duplicate");
    }

    setData(updated);
    setColumns(newCols);
    toast.success("Duplicates marked");
  };

  const fetchTxnData = async () => {
    setLoading(true);
    setProgress(0);

    const allUtrs = data.map((r) => r["Valid UTR"]).filter(Boolean);
    const BATCH = 300;
    let txnMap = {};
    let done = 0;
    toast.info("Fetching transaction data...");
    for (let i = 0; i < allUtrs.length; i += BATCH) {
      const batch = allUtrs.slice(i, i + BATCH).join(",");
      try {
        const res = await getTransactionByUtr(token, batch);
        res.forEach((t) => (txnMap[t.RRN] = t));
      } catch (err) {
        console.error(err);
        toast.error("Error fetching some transaction batches");
      }

      done += BATCH;
      setProgress(Math.min(100, Math.round((done / allUtrs.length) * 100)));
    }

    const updated = data.map((row) => {
      const txn = txnMap[row["Valid UTR"]];
      if (!txn) {
        return {
          ...row,
          "Org Name": "NOT FOUND",
          "Merchant Name": "NOT FOUND",
          PG: "NOT FOUND",
          OrderId: "NOT FOUND",
          "Request Amount": "NOT FOUND",
          "TXN Status": "NOT FOUND",
        };
      }

      return {
        ...row,
        "Org Name": txn["Org Name"],
        "Merchant Name": txn["Merchant"],
        PG: txn["Provider Name"],
        OrderId: txn["OrderId"],
        "Request Amount": txn["Amount"],
        "TXN Status": txn["Status"],
      };
    });

    const extra = [
      "Org Name",
      "Merchant Name",
      "PG",
      "OrderId",
      "Request Amount",
      "TXN Status",
    ];

    const newCols = [...columns];
    extra.forEach((c) => !newCols.includes(c) && newCols.push(c));

    setData(updated);
    setColumns(newCols);
    toast.success("Transaction data fetched successfully");
    setLoading(false);
  };

  const checkChargeback = async () => {
    if (!columns.includes("OrderId")) {
      toast.error("Please fetch transaction details first");
      return;
    }
    setLoading(true);
    setProgress(0);

    const orderIds = data
      .map((r) => r["OrderId"])
      .filter((id) => id && id !== "NOT FOUND");

    const BATCH = 300;
    let cbSet = new Set();
    let done = 0;

    for (let i = 0; i < orderIds.length; i += BATCH) {
      const batch = orderIds.slice(i, i + BATCH).join(",");
      try {
        const res = await isChargebackExists(batch, token);
        res.forEach((r) => cbSet.add(r.orderId));
      } catch {}

      done += BATCH;
      setProgress(Math.min(100, Math.round((done / orderIds.length) * 100)));
    }

    const updated = data.map((row) => {
      const id = row["OrderId"];

      if (!id || id === "NOT FOUND") {
        return { ...row, "Chargeback Status": "NOT FOUND" };
      }

      return {
        ...row,
        "Chargeback Status": cbSet.has(id)
          ? "Found in Chargeback"
          : "Not in Chargeback",
      };
    });

    const newCols = [...columns];
    if (!newCols.includes("Chargeback Status")) {
      newCols.push("Chargeback Status");
    }

    setData(updated);
    setColumns(newCols);
    toast.success("Chargeback check completed");
    setLoading(false);
  };

  const matchAmounts = () => {
    if (!data.length) return;
    if (!columns.includes("Request Amount")) {
      toast.error("Please fetch transaction details first");
      return;
    }
    const bankCol = "Amount"; // fixed as per your requirement
    if (!columns.includes(bankCol)) {
      toast.error("Amount column not found in uploaded file");
      return;
    }

    if (!columns.includes(bankCol)) {
      alert("Amount column not found in uploaded file");
      return;
    }

    const updated = data.map((row) => {
      const bankAmt = parseFloat(row[bankCol]);
      const sysAmt = parseFloat(row["Request Amount"]);

      // Case 1: txn not found OR missing values
      if (
        !row["Request Amount"] ||
        row["Request Amount"] === "NOT FOUND" ||
        isNaN(bankAmt) ||
        isNaN(sysAmt)
      ) {
        return {
          ...row,
          "Amount Match": "NOT FOUND / INVALID",
        };
      }

      // Case 2: match vs mismatch
      return {
        ...row,
        "Amount Match": bankAmt === sysAmt ? "Match" : "Mismatch",
      };
    });

    const newCols = [...columns];
    if (!newCols.includes("Amount Match")) {
      newCols.push("Amount Match");
    }

    setData(updated);
    setColumns(newCols);
  };

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data, { header: columns });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Result");
    XLSX.writeFile(wb, "Final_Reconciliation.xlsx");
    toast.success("File downloaded");
  };

  return (
    <div style={styles.container}>
      <input type="file" onChange={handleFileUpload} />

      {data.length > 0 && (
        <>
          <div>
            <button
              onClick={fetchTxnData}
              style={styles.button}
              disabled={loading}
            >
              Fetch Txn
            </button>
            <button onClick={markDuplicates} style={styles.button}>
              Mark Duplicates
            </button>
            <button
              onClick={checkChargeback}
              style={styles.button}
              disabled={loading}
            >
              Check Chargeback
            </button>
            <button onClick={matchAmounts} style={styles.button}>
              Match Amount
            </button>
            <button onClick={downloadExcel} style={styles.buttonGreen}>
              Download
            </button>
          </div>

          {loading && <p>Processing: {progress}%</p>}

          <div style={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} style={styles.th}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const isNotFound = row["TXN Status"] === "NOT FOUND";
                  const isCB =
                    row["Chargeback Status"] === "Found in Chargeback";
                  const isMismatch = row["Amount Match"] === "Mismatch";

                  return (
                    <tr
                      key={i}
                      style={
                        isNotFound
                          ? styles.notFound
                          : isCB
                            ? styles.chargeback
                            : isMismatch
                              ? styles.amountMismatch
                              : {}
                      }
                    >
                      {columns.map((c, j) => (
                        <td key={j} style={styles.td}>
                          {row[c]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionCheck;
